import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2, MapPin, Phone, MessageSquare } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import SEO from "@/components/SEO";

export default function Enquire() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { settings } = useSettings();
  const presetTour = searchParams.get("tour") || "";

  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    travel_date: "",
    duration: "",
    preferred_tour: presetTour,
    adults: 1,
    children: 0,
    message: "",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [products, setProducts] = useState([]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/products", { params: { limit: 100, sort: "popular" } }).then((r) => {
      const list = Array.isArray(r.data) ? r.data : r.data?.products || [];
      setProducts(list);
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) return toast.error("Please enter your name");
    if (!form.phone.trim()) return toast.error("Please enter your mobile / WhatsApp number");
    if (!form.consent) return toast.error("Please provide consent to contact you");

    setSubmitting(true);
    try {
      await api.post("/enquiries", {
        ...form,
        source: "hero_enquiry",
        product_title: form.preferred_tour || null,
      });
      setSubmitted(true);
    } catch {
      toast.error("Failed to submit. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4" data-testid="enquiry-thank-you">
        <SEO title="Thank You" />
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-display text-3xl mb-3">Thank You!</h1>
          <p className="text-muted-foreground mb-2">
            Your enquiry has been received successfully.
          </p>
          <p className="text-sm text-muted-foreground mb-8">
            Our travel expert will contact you within <span className="font-semibold text-foreground">30 minutes</span> during business hours (9 AM - 8 PM IST).
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate("/")} className="bg-teal hover:bg-teal-dark text-white" data-testid="enquiry-go-home">
              Back to Home
            </Button>
            <Button variant="outline" onClick={() => navigate("/search")} data-testid="enquiry-explore">
              Explore Packages
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] py-10" data-testid="enquiry-page">
      <SEO title="Enquire Now" description="Send us your travel requirements and get a custom quote for tours, hotels, cabs and more in Hyderabad." />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1fr_380px] gap-10">

          {/* Form */}
          <div className="bg-white border border-border rounded-2xl p-6 sm:p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, #00B7EB 0%, #008FA4 100%)" }}>
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-display text-2xl">Enquire Now</h1>
                <p className="text-sm text-muted-foreground">Tell us what you're looking for and we'll get back to you</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={form.full_name}
                    onChange={(e) => set("full_name", e.target.value)}
                    placeholder="Your full name"
                    data-testid="enquiry-name"
                    required
                  />
                </div>
                <div>
                  <Label>Mobile / WhatsApp Number *</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    placeholder="+91 98765 43210"
                    data-testid="enquiry-phone"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Date of Travel</Label>
                  <Input
                    type="date"
                    value={form.travel_date}
                    onChange={(e) => set("travel_date", e.target.value)}
                    min={new Date().toISOString().split("T")[0]}
                    data-testid="enquiry-date"
                  />
                </div>
                <div>
                  <Label>Duration (Days / Nights)</Label>
                  <Select value={form.duration || "_none"} onValueChange={(v) => set("duration", v === "_none" ? "" : v)}>
                    <SelectTrigger data-testid="enquiry-duration"><SelectValue placeholder="Select duration" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Select duration</SelectItem>
                      <SelectItem value="Day trip">Day trip</SelectItem>
                      <SelectItem value="1 Night / 2 Days">1 Night / 2 Days</SelectItem>
                      <SelectItem value="2 Nights / 3 Days">2 Nights / 3 Days</SelectItem>
                      <SelectItem value="3 Nights / 4 Days">3 Nights / 4 Days</SelectItem>
                      <SelectItem value="4 Nights / 5 Days">4 Nights / 5 Days</SelectItem>
                      <SelectItem value="5 Nights / 6 Days">5 Nights / 6 Days</SelectItem>
                      <SelectItem value="6 Nights / 7 Days">6 Nights / 7 Days</SelectItem>
                      <SelectItem value="7+ Nights">7+ Nights</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Preferred Tour / Package</Label>
                <Select value={form.preferred_tour || "_none"} onValueChange={(v) => set("preferred_tour", v === "_none" ? "" : v)}>
                  <SelectTrigger data-testid="enquiry-tour"><SelectValue placeholder="Select a tour" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">-- Select a package (optional) --</SelectItem>
                    {products.map((p) => (
                      <SelectItem key={p.id} value={p.title}>{p.title}</SelectItem>
                    ))}
                    <SelectItem value="Custom / Other">Custom / Other requirement</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>No. of Adults</Label>
                  <Input
                    type="number"
                    min={1}
                    value={form.adults}
                    onChange={(e) => set("adults", parseInt(e.target.value) || 1)}
                    data-testid="enquiry-adults"
                  />
                </div>
                <div>
                  <Label>No. of Children</Label>
                  <Input
                    type="number"
                    min={0}
                    value={form.children}
                    onChange={(e) => set("children", parseInt(e.target.value) || 0)}
                    data-testid="enquiry-children"
                  />
                </div>
              </div>

              <div>
                <Label>Comments / Special Requirements</Label>
                <Textarea
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  placeholder="Tell us about your travel plans, budget, special requests..."
                  rows={3}
                  data-testid="enquiry-message"
                />
              </div>

              <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-xl">
                <Checkbox
                  id="consent"
                  checked={form.consent}
                  onCheckedChange={(v) => set("consent", !!v)}
                  data-testid="enquiry-consent"
                />
                <label htmlFor="consent" className="text-sm leading-relaxed cursor-pointer">
                  I consent to {settings.site_name || "HyderabadTourism.in"} contacting me via <strong>phone, WhatsApp, SMS, or email</strong> regarding this enquiry. *
                </label>
              </div>

              <Button
                type="submit"
                className="w-full bg-saffron hover:bg-saffron-dark text-white h-12 text-base"
                disabled={submitting}
                data-testid="enquiry-submit"
              >
                {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Send className="w-5 h-5 mr-2" />}
                {submitting ? "Submitting..." : "Send Enquiry"}
              </Button>
            </form>
          </div>

          {/* Sidebar */}
          <div className="hidden lg:block space-y-6">
            <div className="bg-white border border-border rounded-2xl p-6 shadow-sm">
              <h3 className="font-display text-lg mb-4">Why Enquire With Us?</h3>
              <ul className="space-y-4 text-sm">
                <li className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                  <span><strong>Free consultation</strong> — No obligation, no hidden charges</span>
                </li>
                <li className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                  <span><strong>Custom packages</strong> — Tailored to your budget and preferences</span>
                </li>
                <li className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                  <span><strong>Quick response</strong> — Callback within 30 minutes (9 AM - 8 PM)</span>
                </li>
                <li className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                  <span><strong>{settings.trust_badges?.badge1_title || `Est. ${settings.trust_badges?.est_year || "2005"}`}</strong> — {settings.trust_badges?.badge4_title ? `Trusted by ${settings.trust_badges.badge4_title}` : `Trusted by ${settings.trust_badges?.happy_customers || "10,000+"} travellers`}</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-teal to-teal-dark rounded-2xl p-6 text-white">
              <h3 className="font-display text-lg mb-2">Prefer to talk?</h3>
              <p className="text-white/80 text-sm mb-4">Call us directly for instant assistance</p>
              <a href={`tel:${(settings.contact_phone || "").replace(/\s/g, "")}`} className="flex items-center gap-2 text-lg font-semibold">
                <Phone className="w-5 h-5" /> {settings.contact_phone || "+91 90000 00000"}
              </a>
              <div className="flex items-center gap-2 mt-3 text-sm text-white/80">
                <MapPin className="w-4 h-4" /> Hyderabad, Telangana
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
